import { useEffect, useState } from "react";
import { api, apiUrl, apiErrorMessage, fmtINR, fmtCompact, fmtPeriod } from "@/lib/api";
import { PageHeader, Section, Btn, Badge } from "@/components/Shared";
import { Lock, Unlock, FileDown } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

export default function PeriodClose() {
  const [closes, setCloses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [c, v] = await Promise.all([api.get("/period-closes"), api.get("/volumes")]);
    setCloses(c.data);
    setPeriods([...new Set(v.data.map((x) => x.period))].sort());
  };
  useEffect(() => { load(); }, []);

  const closedSet = new Set(closes.map((c) => c.period));

  const close = async (period) => {
    if (!await openConfirm({
      title: `Close ${fmtPeriod(period)}?`,
      message: "Volumes for this period will become read-only and a snapshot is created so partner statements stay reproducible.",
      confirmLabel: "Close period",
    })) return;
    setLoading(true);
    try { await api.post(`/period-closes/${period}`); toast.success(`${fmtPeriod(period)} closed`); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed to close period")); }
    setLoading(false);
  };
  const reopen = async (period) => {
    if (!await openConfirm({
      title: `Reopen ${fmtPeriod(period)}?`,
      message: "The snapshot will be discarded — any PDF or recon already generated for this period becomes invalid.",
      confirmLabel: "Reopen period",
      danger: true,
    })) return;
    try { await api.delete(`/period-closes/${period}`); toast.success(`${fmtPeriod(period)} reopened`); setDetail(null); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed to reopen")); }
  };
  const showDetail = async (period) => {
    try { const { data } = await api.get(`/period-closes/${period}/detail`); setDetail(data); }
    catch (e) { toast.error("Could not load detail"); }
  };
  const downloadPdf = (period, partner_id) => {
    window.open(apiUrl(`/period-closes/${period}/pdf/${partner_id}`), "_blank");
  };

  return (
    <div data-testid="period-close-page">
      <PageHeader
        title="Period Close"
      />
      <div className="p-4 sm:p-10 space-y-6">
        <Section title="All Periods" testid="periods-section">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr><th>Period</th><th>Status</th><th>Closed at</th><th className="text-right">Revenue</th><th className="text-right">Payout</th><th className="text-right">Net Revenue</th><th className="text-right">Volume</th><th></th></tr></thead>
            <tbody>
              {periods.map((p) => {
                const cl = closes.find((c) => c.period === p);
                return (
                  <tr key={p} data-testid={`period-row-${p}`}>
                    <td className="num font-medium">{fmtPeriod(p)}</td>
                    <td>{cl ? <Badge tone="positive">Closed</Badge> : <Badge tone="muted">Open</Badge>}</td>
                    <td className="text-[12px]">{cl ? new Date(cl.closed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                    <td className="text-right num">{cl ? `₹ ${fmtCompact(cl.totals.gross)}` : "—"}</td>
                    <td className="text-right num text-[var(--accent)]">{cl ? `₹ ${fmtCompact(cl.totals.payout)}` : "—"}</td>
                    <td className="text-right num text-[var(--positive)]">{cl ? `₹ ${fmtCompact(cl.totals.net)}` : "—"}</td>
                    <td className="text-right num">{cl ? `₹ ${fmtCompact(cl.totals.volume)}` : "—"}</td>
                    <td className="text-right">
                      {cl ? (
                        <div className="flex justify-end gap-2">
                          <Btn variant="ghost" testid={`view-${p}`} onClick={() => showDetail(p)}><FileDown size={13} /> PDFs</Btn>
                          <Btn variant="danger" testid={`reopen-${p}`} onClick={() => reopen(p)}><Unlock size={13} /> Reopen</Btn>
                        </div>
                      ) : (
                        <Btn variant="primary" testid={`close-${p}`} disabled={loading} onClick={() => close(p)}><Lock size={13} /> Close period</Btn>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!periods.length && <tr><td colSpan={8} className="text-center text-[var(--muted)] py-6">No volume data yet.</td></tr>}
            </tbody>
          </table></div>
        </Section>

        {detail && (
          <Section title={`Partner Statements — ${fmtPeriod(detail.period)}`} testid="detail-section"
            right={<Btn variant="ghost" onClick={() => setDetail(null)}>Close</Btn>}>
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr><th>Partner</th><th className="text-right">Revenue</th><th className="text-right">Payout</th><th className="text-right">Net Revenue</th><th className="text-right">Rows</th><th></th></tr></thead>
              <tbody>
                {detail.partner_totals.map((pt) => (
                  <tr key={pt.partner_id}>
                    <td><div className="font-medium">{pt.partner_name}</div><div className="text-[11px] text-[var(--muted)] num">{pt.partner_id}</div></td>
                    <td className="text-right num">₹ {fmtINR(pt.gross, 0)}</td>
                    <td className="text-right num text-[var(--accent)] font-medium">₹ {fmtINR(pt.payout, 0)}</td>
                    <td className="text-right num text-[var(--positive)]">₹ {fmtINR(pt.net, 0)}</td>
                    <td className="text-right num">{pt.rows}</td>
                    <td className="text-right">
                      <Btn variant="accent" testid={`download-${detail.period}-${pt.partner_id}`} onClick={() => downloadPdf(detail.period, pt.partner_id)}>
                        <FileDown size={13} /> Download PDF
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </Section>
        )}
      </div>
    </div>
  );
}
